import React, { useRef, useState, useEffect, memo, useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import KanbanCard from '../KanbanCard';
import SortableCard from '../SortableCard';

/**
 * ColumnCards Component
 * Renders the scrollable cards container with sortable cards and scroll indicator
 */
const ColumnCards = memo(({
  column,
  tasks,
  taskIds,
  categories,
  allTags,
  allTasks,
  nestedSprints,
  statuses,
  lockStates,
  selectedTaskIds,
  cardDisplaySettings,
  editingCardId,
  setEditingCardId,
  isAnyColumnDragging,
  isAnyCardDragging = false, // PERFORMANCE: Virtualize cards during card drag too
  isDragOverlay,
  isCollapsed,
  visibleCardCount = 8, // How many cards fit in viewport
  frozenHiddenCount, // Frozen hidden count during drag
  // Card event handlers
  onTaskClick,
  onOpenDetails,
  onOpenAttachments,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onOpenNestedSprint,
  onCreateNestedSprint,
  onRemoveNestedSprint,
  onIconChange,
  onRename,
  onStatusChange,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onPriorityChange,
  onDifficultyChange,
  onCustomFieldValueChange,
  customFields = [],
  onDeleteTask,
  onCopyCard,
  onDuplicateCard,
  onCreateParallelMission,
  onInsertMissionBefore,
  onSetStatusAutoSync,
  onDeliverablesChange,
  onInputsChange,
  onArchiveTask,
  onUnarchiveTask,
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  // View navigation (for "Open in view" button)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Deliverables warning modal
  onSubmitDeliverable,
  // Deliverables editing (for conditions modal)
  onEditDeliverables,
  onClearDeliverable,
  onNavigateToParentMission,
  // Template mode
  isTemplate = false,
}) => {
  // Ref for cards container to detect overflow
  const cardsContainerRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  // Check for overflow and count hidden cards below viewport
  useEffect(() => {
    const container = cardsContainerRef.current;
    if (!container || isCollapsed) {
      setHasOverflow(false);
      setHiddenCount(0);
      return;
    }

    let rafId = null;
    let lastCheck = 0;
    const THROTTLE_MS = 150; // Throttle to avoid interrupting smooth scroll

    const checkOverflow = (force = false) => {
      const now = Date.now();
      if (!force && now - lastCheck < THROTTLE_MS) {
        // Schedule a check after throttle period if not already scheduled
        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            rafId = null;
            checkOverflow();
          });
        }
        return;
      }
      lastCheck = now;

      const hasScroll = container.scrollHeight > container.clientHeight;
      const isNotAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight > 20;
      setHasOverflow(hasScroll && isNotAtBottom);
      
      // Count cards below viewport
      if (hasScroll && isNotAtBottom) {
        const cards = container.querySelectorAll('.sortable-card-wrapper');
        const containerBottom = container.getBoundingClientRect().bottom;
        let hiddenBelow = 0;
        
        cards.forEach(card => {
          if (card.getBoundingClientRect().top > containerBottom) {
            hiddenBelow++;
          }
        });
        
        setHiddenCount(hiddenBelow);
      } else {
        setHiddenCount(0);
      }
    };

    // Delay initial check to allow scroll-to-new-card to complete first
    const initialTimer = setTimeout(() => {
      checkOverflow(true);
    }, 200);

    // Check on scroll (throttled)
    container.addEventListener('scroll', checkOverflow, { passive: true });
    
    // Check on resize
    const resizeObserver = new ResizeObserver(() => checkOverflow());
    resizeObserver.observe(container);

    return () => {
      clearTimeout(initialTimer);
      container.removeEventListener('scroll', checkOverflow);
      resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isCollapsed, tasks.length, isAnyColumnDragging, isAnyCardDragging]); // Re-run when any drag ends to recalculate

  // Make the cards container a droppable area for cards
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `column-drop-${column.id}`,
    data: {
      type: 'column',
      columnId: column.id,
    },
  });

  // Stable callback to clear editing state (avoids inline arrow in render)
  const handleFinishTitleEdit = useCallback(() => {
    setEditingCardId(null);
  }, [setEditingCardId]);

  // Performance optimization: don't render cards when column is collapsed
  if (isCollapsed) {
    return null;
  }

  return (
    <>
      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        <div 
          ref={(node) => {
            setDroppableRef(node);
            cardsContainerRef.current = node;
          }}
          className={`kanban-column__cards ${isOver ? 'kanban-column__cards--drag-over' : ''}`}
        >
          {tasks.map((task, index) => {
            // PERFORMANCE: During ANY drag (column OR card), only render visible cards
            // Columns scroll to top, so we unload cards below the viewport
            const isAnyDragging = isAnyColumnDragging || isAnyCardDragging;
            if (isAnyDragging && !isDragOverlay && index >= visibleCardCount) {
              return null;
            }
            
            const category = task.category 
              ? categories.find(c => c.name === task.category) 
              : null;
            
            const nestedSprint = task.nestedSprintId 
              ? nestedSprints?.find(s => s.id === task.nestedSprintId)
              : null;
            
            const taskStatus = statuses.find(s => s.id === task.status);
            const isBlocked = taskStatus?.category === 'blocked';
            
            return (
              <SortableCard key={task.id} id={task.id}>
                <KanbanCard
                  task={task}
                  category={category}
                  allCategories={categories}
                  allTags={allTags}
                  allTasks={allTasks}
                  statuses={statuses}
                  nestedSprint={nestedSprint}
                  isSelected={selectedTaskIds.has(task.id)}
                  isEditingTitle={editingCardId === task.id}
                  onFinishTitleEdit={handleFinishTitleEdit}
                  onSelect={onTaskClick}
                  onOpenDetails={onOpenDetails}
                  onOpenAttachments={onOpenAttachments}
                  onCategoryChange={onCategoryChange}
                  onRenameCategory={onRenameCategory}
                  onDeleteCategory={onDeleteCategory}
                  onAddTag={onAddTag}
                  onRemoveTag={onRemoveTag}
                  onSetPrimaryTag={onSetPrimaryTag}
                  onRenameTag={onRenameTag}
                  onDeleteTag={onDeleteTag}
                  onStatusChange={onStatusChange}
                  onOpenNestedSprint={onOpenNestedSprint}
                  onCreateNestedSprint={onCreateNestedSprint}
                  onRemoveNestedSprint={() => onRemoveNestedSprint?.(task.id)}
                  onIconChange={onIconChange}
                  onRename={onRename}
                  onDueDateChange={onDueDateChange}
                  onRemindersChange={onRemindersChange}
                  onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
                  onCreatedAtChange={onCreatedAtChange}
                  onStartedAtChange={onStartedAtChange}
                  onCompletedAtChange={onCompletedAtChange}
                  onUpdatedAtChange={onUpdatedAtChange}
                  onPriorityChange={onPriorityChange}
                  onDifficultyChange={onDifficultyChange}
                  onCustomFieldValueChange={onCustomFieldValueChange}
                  customFields={customFields}
                  onDeleteTask={onDeleteTask}
                  onCopyCard={onCopyCard}
                  onDuplicateCard={onDuplicateCard}
                  onCreateParallelMission={onCreateParallelMission}
                  onInsertMissionBefore={onInsertMissionBefore}
                  onSetStatusAutoSync={onSetStatusAutoSync}
                  onDeliverablesChange={onDeliverablesChange}
                  onInputsChange={onInputsChange}
                  onArchiveTask={onArchiveTask}
                  onUnarchiveTask={onUnarchiveTask}
                  onDecisionAnswerChange={onDecisionAnswerChange}
                  onDecisionOptionsChange={onDecisionOptionsChange}
                  isBlocked={isBlocked}
                  lockState={lockStates?.[task.id]}
                  cardDisplaySettings={cardDisplaySettings}
                  isTemplate={isTemplate}
                  onAddStatus={onAddStatus}
                  onDeleteStatus={onDeleteStatus}
                  onRenameStatus={onRenameStatus}
                  currentView={currentView}
                  customViews={customViews}
                  activeCustomViewId={activeCustomViewId}
                  onViewChange={onViewChange}
                  onSelectCustomView={onSelectCustomView}
                  onGoToTaskInView={onGoToTaskInView}
                  onSubmitDeliverable={onSubmitDeliverable}
                  onEditDeliverables={onEditDeliverables}
                  onClearDeliverable={onClearDeliverable}
                  onNavigateToParentMission={onNavigateToParentMission}
                />
              </SortableCard>
            );
          })}
          
          {tasks.length === 0 && !isOver && (
            <div className="kanban-column__empty">
              {column.category === 'completed' ? 'No completed tasks yet' : 'Drop tasks here'}
            </div>
          )}
          
          {isOver && tasks.length === 0 && (
            <div className="kanban-column__drop-placeholder">
              Drop here
            </div>
          )}
        </div>
      </SortableContext>
      
      {/* Scroll indicator - shows when there's content below, clickable to scroll */}
      {/* During ANY drag (column or card), show frozen count; otherwise show dynamic count */}
      <div 
        className={`kanban-column__scroll-indicator ${
          (isAnyColumnDragging || isAnyCardDragging)
            ? (frozenHiddenCount > 0 ? '' : 'kanban-column__scroll-indicator--hidden')
            : (hasOverflow ? '' : 'kanban-column__scroll-indicator--hidden')
        }`}
        onClick={() => {
          if (isAnyColumnDragging || isAnyCardDragging) return; // No scrolling during drag
          const container = cardsContainerRef.current;
          if (container) {
            const scrollAmount = container.clientHeight * 0.8;
            const remainingScroll = container.scrollHeight - container.scrollTop - container.clientHeight;
            container.scrollBy({ 
              top: Math.min(scrollAmount, remainingScroll), 
              behavior: 'smooth' 
            });
          }
        }}
      >
        <div className="kanban-column__scroll-indicator-badge">
          {((isAnyColumnDragging || isAnyCardDragging) ? frozenHiddenCount : hiddenCount) > 0 
            ? `+${(isAnyColumnDragging || isAnyCardDragging) ? frozenHiddenCount : hiddenCount} more` 
            : 'Scroll'}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>
    </>
  );
});

export default ColumnCards;

